import { getAuth } from 'firebase/auth';
import Logger from '../../utils/Logger';
import config from '../../utils/EnvironmentSwitcher';

const TOKEN_REFRESH_BUFFER_MS = 2 * 60 * 1000;
const tokenCache = {
  uid: null,
  token: null,
  expiresAtMs: 0,
};

const parseJwtExpiry = (token) => {
  try {
    if (!token || typeof token !== 'string') return 0;
    const [, payloadPart] = token.split('.');
    if (!payloadPart) return 0;
    const base64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const payload = JSON.parse(atob(padded));
    const expSeconds = Number(payload?.exp || 0);
    return Number.isFinite(expSeconds) && expSeconds > 0 ? expSeconds * 1000 : 0;
  } catch {
    return 0;
  }
};

export default class BaseMarketplaceService {
  constructor(baseUrl = config.apiUrl) {
    this.baseUrl = baseUrl;
  }

  async getAuthHeaders(options = {}) {
    const { forceRefresh = false } = options;
    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        Logger.error('getAuthHeaders: No current user found');
        throw new Error('User not authenticated');
      }

      // Clear cache if user switched.
      if (tokenCache.uid && tokenCache.uid !== user.uid) {
        tokenCache.uid = null;
        tokenCache.token = null;
        tokenCache.expiresAtMs = 0;
      }

      const now = Date.now();
      const hasValidCachedToken =
        !forceRefresh &&
        tokenCache.uid === user.uid &&
        !!tokenCache.token &&
        tokenCache.expiresAtMs - now > TOKEN_REFRESH_BUFFER_MS;

      if (hasValidCachedToken) {
        return {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokenCache.token}`,
        };
      }

      // Only force refresh when explicitly requested or cached token is near expiration.
      const shouldForceRefresh =
        forceRefresh ||
        (tokenCache.uid === user.uid && tokenCache.expiresAtMs - now <= TOKEN_REFRESH_BUFFER_MS);

      const token = await user.getIdToken(shouldForceRefresh);
      if (!token) {
        Logger.error('getAuthHeaders: Failed to get ID token');
        throw new Error('Failed to get authentication token');
      }

      tokenCache.uid = user.uid;
      tokenCache.token = token;
      tokenCache.expiresAtMs = parseJwtExpiry(token) || now + 55 * 60 * 1000;

      return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      };
    } catch (error) {
      Logger.error('Error getting auth headers:', error);
      throw error;
    }
  }
}
